import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  register as apiRegister,
  login as apiLogin,
  RegisterPayload,
  LoginPayload,
  AuthResponse,
} from '../api/authApi';
import { OrganizationInfo } from '../utils/planConfig';
import { setAxiosAuth } from '../api/axiosInstance';

interface AuthContextType {
  user: AuthResponse['user'] | null;
  organization: OrganizationInfo | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (payload: LoginPayload) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  setOrganization: (org: OrganizationInfo) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function persistAuth(res: AuthResponse) {
  if (res.user && res.token) {
    localStorage.setItem('token', res.token);
    localStorage.setItem('user', JSON.stringify(res.user));
    if (res.organization) {
      localStorage.setItem('organization', JSON.stringify(res.organization));
    }
    setAxiosAuth(res.user._id, res.token);
    if (!localStorage.getItem('activeTab')) {
      localStorage.setItem('activeTab', 'overview');
    }
  }
}

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthResponse['user'] | null>(null);
  const [organization, setOrganizationState] = useState<OrganizationInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedOrg = localStorage.getItem('organization');
    if (storedToken) setToken(storedToken);
    if (storedUser) setUser(JSON.parse(storedUser));
    if (storedOrg) setOrganizationState(JSON.parse(storedOrg));
    if (storedToken && storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setAxiosAuth(parsedUser._id, storedToken);
    }
  }, []);

  const setOrganization = (org: OrganizationInfo) => {
    setOrganizationState(org);
    localStorage.setItem('organization', JSON.stringify(org));
  };

  const handleAuthSuccess = (res: AuthResponse) => {
    if (res.user && res.token) {
      setUser(res.user);
      setToken(res.token);
      if (res.organization) setOrganizationState(res.organization);
      persistAuth(res);
    } else {
      setError(res.message);
    }
  };

  const login = async (payload: LoginPayload) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiLogin(payload);
      handleAuthSuccess(res);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload: RegisterPayload) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiRegister(payload);
      handleAuthSuccess(res);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setOrganizationState(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('organization');
    setAxiosAuth(null, null);
    localStorage.removeItem('activeTab');
    navigate('/auth');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        token,
        loading,
        error,
        login,
        register,
        setOrganization,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthContextProvider');
  return context;
};
